// src/components/ChatSidebar.jsx
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { ListGroup, Badge } from "react-bootstrap";

const OnlineDot = ({ on }) => (
  <span
    aria-hidden="true"
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: on ? "#52c41a" : "#d9d9d9",
      marginRight: 8,
      flex: "0 0 auto",
    }}
  />
);

const formatWhen = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "2-digit" });
};

const lastMessageOf = (session) => {
  const msgs = Array.isArray(session?.messages) ? session.messages : [];
  return msgs.length ? msgs[msgs.length - 1] : null;
};

const snippet = (text, n = 70) => {
  if (!text) return "";
  const clean = String(text).replace(/\s+/g, " ").trim();
  return clean.length > n ? `${clean.slice(0, n)}…` : clean;
};

const ChatSidebar = ({
  sessions,
  activeSessionId,
  onSelectSession,
  onlineRooms = {},
}) => {
  const ordered = useMemo(() => {
    const list = Array.isArray(sessions) ? sessions.slice() : [];
    return list.sort((a, b) => {
      const at = lastMessageOf(a)?.timestamp || 0;
      const bt = lastMessageOf(b)?.timestamp || 0;
      return new Date(bt) - new Date(at);
    });
  }, [sessions]);

  if (!ordered.length) {
    return (
      <ListGroup style={{ maxHeight: 400, overflowY: "auto" }}>
        <ListGroup.Item disabled>No chats yet</ListGroup.Item>
      </ListGroup>
    );
  }

  return (
    <ListGroup style={{ maxHeight: 400, overflowY: "auto" }}>
      {ordered.map((session) => {
        const { sessionId } = session;
        const roomNumber = session?.roomNumber ?? "";
        const guestName = session?.guestName || "Guest";
        const last = lastMessageOf(session);
        const online = roomNumber
          ? (onlineRooms[String(roomNumber)] ?? 0) > 0
          : false;

        // Prefix for who sent the last message
        const prefix =
          last?.sender && String(last.sender).toLowerCase() === "staff"
            ? "You: "
            : "";

        return (
          <ListGroup.Item
            key={sessionId}
            active={sessionId === activeSessionId}
            onClick={() => onSelectSession(sessionId)}
            action
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
              paddingTop: 10,
              paddingBottom: 10,
            }}
          >
            <OnlineDot on={online} />

            <div style={{ minWidth: 0, flex: "1 1 auto" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  lineHeight: 1.2,
                }}
              >
                <strong style={{ whiteSpace: "nowrap" }}>
                  Room {roomNumber || "?"}
                </strong>
                <span
                  style={{
                    color: "var(--bs-secondary-color, #6c757d)",
                    fontSize: 12,
                  }}
                >
                  — {guestName}
                </span>
                {online && (
                  <Badge bg="success" pill style={{ marginLeft: "auto" }}>
                    Online
                  </Badge>
                )}
                {!online && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: 12,
                      color: "var(--bs-secondary-color, #6c757d)",
                    }}
                  >
                    {formatWhen(last?.timestamp)}
                  </span>
                )}
              </div>

              <div
                title={last?.text || ""}
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  color: "var(--bs-secondary-color, #6c757d)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {last ? `${prefix}${snippet(last.text)}` : "No messages yet"}
              </div>
            </div>
          </ListGroup.Item>
        );
      })}
    </ListGroup>
  );
};

ChatSidebar.propTypes = {
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      sessionId: PropTypes.string.isRequired,
      roomNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      guestName: PropTypes.string,
      messages: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string,
          sender: PropTypes.string,
          timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
      ),
    })
  ).isRequired,
  activeSessionId: PropTypes.string,
  onSelectSession: PropTypes.func.isRequired,
  // Map of roomNumber -> count of connected clients
  onlineRooms: PropTypes.object,
};

export default ChatSidebar;
